import { orderCanCancel, orderCanRevertToDraft, orderLinesEditable } from "@bt/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  BoxesIcon,
  ExternalLinkIcon,
  MoreHorizontalIcon,
  PackageCheckIcon,
  PencilIcon,
  PlusIcon,
  PrinterIcon,
  RotateCcwIcon,
  SendIcon,
  Trash2Icon,
  Undo2Icon,
  XCircleIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DocumentList } from "@/components/app/attachments";
import { FileImage } from "@/components/app/file-image";
import { LinksPanel } from "@/components/app/links";
import { Page, PageHeader, SectionTitle } from "@/components/app/page";
import { PartPicker } from "@/components/app/part-picker";
import { MoneyInput, QuantityInput } from "@/components/app/qty";
import { OrderStatusBadge } from "@/components/app/status";
import { Stepper } from "@/components/app/stepper";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/misc";
import { PlaceOrderDialog, ReceiveDialog } from "@/features/orders/order-dialogs";
import { OrderSheet } from "@/features/orders/order-sheet";
import { PartSheet } from "@/features/parts/part-sheet";
import { PartsListDialog } from "@/features/parts-list/parts-list-dialog";
import { openExternal } from "@/lib/desktop";
import { useFormat } from "@/lib/format";
import { usePriceBasis } from "@/lib/prices";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/orders/$orderId")({
  component: OrderPage,
});

type Order = RouterOutputs["orders"]["get"];
type Line = Order["lines"][number];

const STEPS = ["draft", "ordered", "received"] as const;

function OrderPage() {
  const { orderId } = Route.useParams();
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const navigate = useNavigate();
  const { data: order, isLoading } = useQuery(trpc.orders.get.queryOptions({ id: orderId }));
  const priceBasis = usePriceBasis(order?.taxRateBp);
  const [editing, setEditing] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [listing, setListing] = useState(false);
  const [creatingPart, setCreatingPart] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<"delete" | "cancel" | null>(null);
  const addLine = useMutation(trpc.orders.addLine.mutationOptions());
  const receiveAll = useMutation(trpc.orders.receiveAll.mutationOptions());
  const revert = useMutation(trpc.orders.revertToDraft.mutationOptions());
  const cancel = useMutation(trpc.orders.cancel.mutationOptions());
  const remove = useMutation(trpc.orders.delete.mutationOptions());

  if (isLoading || !order) {
    return (
      <Page>
        <Skeleton className="h-64 rounded-xl" />
      </Page>
    );
  }

  const editable = orderLinesEditable(order.status);
  const receivable = order.status === "ordered" || order.status === "partially_received";
  const stepIndex =
    order.status === "draft"
      ? 0
      : order.status === "received"
        ? 2
        : order.status === "cancelled"
          ? -1
          : 1;
  const add = (partId: string) =>
    addLine.mutate({
      orderId: order.id,
      data: { partId, qtyOrdered: 1000, unitPriceMinor: null, workOrderId: null, note: null },
    });

  return (
    <Page>
      <PageHeader
        crumbs={[{ label: t("nav.orders"), to: "/orders" }]}
        title={order.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <OrderStatusBadge status={order.status} />
            {order.supplier ? <span>{order.supplier.name}</span> : null}
            {order.orderNumber ? (
              <span className="font-mono text-xs">{order.orderNumber}</span>
            ) : null}
          </span>
        }
        actions={
          <>
            {order.status === "draft" ? (
              <Button onClick={() => setPlacing(true)} disabled={order.lines.length === 0}>
                <SendIcon />
                {t("orders.place")}
              </Button>
            ) : null}
            {receivable ? (
              <>
                <Button variant="outline" onClick={() => setReceiving(true)}>
                  {t("orders.receive")}
                </Button>
                <Button
                  onClick={() =>
                    receiveAll.mutate(
                      { id: order.id, receivedOn: null },
                      { onSuccess: () => toast.success(t("orders.receivedToast")) },
                    )
                  }
                  disabled={receiveAll.isPending}
                >
                  <PackageCheckIcon />
                  {t("orders.receiveAll")}
                </Button>
              </>
            ) : null}
            {order.status === "cancelled" ? (
              <Button variant="outline" onClick={() => revert.mutate({ id: order.id })}>
                <RotateCcwIcon />
                {t("orders.reopenDraft")}
              </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t("common.more")}>
                  <MoreHorizontalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setEditing(true)}>
                  <PencilIcon />
                  {t("common.edit")}
                </DropdownMenuItem>
                {order.lines.length ? (
                  <DropdownMenuItem onSelect={() => setListing(true)}>
                    <PrinterIcon />
                    {t("partsList.print")}
                  </DropdownMenuItem>
                ) : null}
                {order.supplier?.website ? (
                  <DropdownMenuItem
                    onSelect={() => openExternal(order.supplier?.website as string)}
                  >
                    <ExternalLinkIcon />
                    {order.supplier.name}
                  </DropdownMenuItem>
                ) : null}
                {orderCanRevertToDraft(order.status, order.anyReceived) &&
                order.status !== "cancelled" ? (
                  <DropdownMenuItem onSelect={() => revert.mutate({ id: order.id })}>
                    <RotateCcwIcon />
                    {t("orders.revertToDraft")}
                  </DropdownMenuItem>
                ) : null}
                {orderCanCancel(order.status, order.anyReceived) ? (
                  <DropdownMenuItem onSelect={() => setConfirm("cancel")}>
                    <XCircleIcon />
                    {t("orders.cancelOrder")}
                  </DropdownMenuItem>
                ) : null}
                {!order.anyReceived ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem destructive onSelect={() => setConfirm("delete")}>
                      <Trash2Icon />
                      {t("common.delete")}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {stepIndex >= 0 ? (
        <Stepper
          className="mb-6"
          label={t("common.status")}
          current={stepIndex}
          steps={STEPS.map((step, index) => {
            const date =
              step === "ordered" ? order.orderedOn : step === "received" ? order.receivedOn : null;
            return {
              key: step,
              label:
                step === "ordered" && order.status === "partially_received"
                  ? t("orderStatus.partially_received")
                  : t(`orderStatus.${step}`),
              detail: date ? format.shortDate(date) : null,
              done: index < stepIndex || (index === stepIndex && step === "received"),
            };
          })}
        />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        <div className="flex min-w-0 flex-col gap-7">
          <Card className="overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle>
                {t("orders.lines")}
                <span className="ml-2 font-normal text-muted-foreground tabular">
                  {order.lines.length || ""}
                </span>
              </CardTitle>
              {editable ? (
                <PartPicker
                  onSelect={(part) => add(part.id)}
                  onCreate={(name) => setCreatingPart(name)}
                  trigger={
                    <Button variant="outline" size="sm">
                      <PlusIcon />
                      {t("orders.addLine")}
                    </Button>
                  }
                />
              ) : null}
            </CardHeader>
            {order.lines.length ? (
              <ul className="divide-y divide-border border-t border-border">
                {order.lines.map((line) => (
                  <OrderLineRow key={line.id} line={line} order={order} editable={editable} />
                ))}
              </ul>
            ) : (
              <CardContent>
                <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                  {t("orders.linesEmpty")}
                </p>
              </CardContent>
            )}
          </Card>
          <div className="grid gap-7 md:grid-cols-2">
            <section>
              <SectionTitle>{t("common.documents")}</SectionTitle>
              <DocumentList ownerType="order" ownerId={order.id} attachments={order.attachments} />
            </section>
            <section>
              <SectionTitle>{t("common.links")}</SectionTitle>
              <LinksPanel ownerType="order" ownerId={order.id} links={order.links} />
            </section>
          </div>
        </div>

        <aside className="flex flex-col gap-4">
          <Card>
            <CardContent className="flex flex-col gap-2 pt-4 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>{t("orders.subtotal")}</span>
                <span className="tabular">
                  {format.money(order.linesTotalMinor, order.currency)}
                </span>
              </div>
              {order.shippingMinor ? (
                <div className="flex justify-between text-muted-foreground">
                  <span>{t("orders.shipping")}</span>
                  <span className="tabular">
                    {format.money(order.shippingMinor, order.currency)}
                  </span>
                </div>
              ) : null}
              <div className="flex justify-between border-t border-border pt-2 text-base font-semibold">
                <span>
                  {t("common.total")}{" "}
                  <span className="text-xs font-normal text-muted-foreground">({priceBasis})</span>
                </span>
                <span className="tabular">{format.money(order.totalMinor, order.currency)}</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex flex-col gap-3 pt-4 text-sm">
              {(
                [
                  [t("orders.orderNumber"), order.orderNumber],
                  [t("orders.orderedOn"), order.orderedOn ? format.date(order.orderedOn) : null],
                  [t("orders.expectedOn"), order.expectedOn ? format.date(order.expectedOn) : null],
                  [t("orders.receivedOn"), order.receivedOn ? format.date(order.receivedOn) : null],
                  [
                    t("orders.placedBy"),
                    order.placedBy ? (order.placedBy.name ?? t("users.formerUser")) : null,
                  ],
                ] as const
              ).map(([label, value]) =>
                value ? (
                  <div key={label} className="flex justify-between gap-3">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="truncate font-medium">{value}</span>
                  </div>
                ) : null,
              )}
              {order.trackingUrl ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openExternal(order.trackingUrl as string)}
                >
                  <ExternalLinkIcon />
                  {t("orders.trackingUrl")}
                </Button>
              ) : null}
              {order.notes ? (
                <p className="rounded-lg bg-muted/60 px-3 py-2 whitespace-pre-wrap">
                  {order.notes}
                </p>
              ) : null}
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                <PencilIcon />
                {t("common.edit")}
              </Button>
            </CardContent>
          </Card>
        </aside>
      </div>

      <OrderSheet open={editing} onOpenChange={setEditing} order={order} />
      <PartsListDialog
        open={listing}
        onOpenChange={setListing}
        context={order.orderNumber ? `${order.name} · ${order.orderNumber}` : order.name}
        dealerId={order.supplier?.id ?? null}
        items={order.lines.map((line) => ({
          partId: line.partId,
          name: line.partName,
          unit: line.unit,
          primaryNumber: line.primaryNumber,
          qty: line.qtyOrdered,
          workOrderIds: line.workOrder ? [line.workOrder.id] : [],
          hint: line.workOrder ? `${line.workOrder.title} · ${line.workOrder.vehicleName}` : null,
          selected: true,
          orderPriceMinor: line.unitPriceMinor,
        }))}
      />
      <PlaceOrderDialog
        open={placing}
        onOpenChange={setPlacing}
        orderId={order.id}
        orderNumber={order.orderNumber}
        expectedOn={order.expectedOn}
      />
      <ReceiveDialog
        open={receiving}
        onOpenChange={setReceiving}
        orderId={order.id}
        lines={order.lines}
      />
      <PartSheet
        open={creatingPart !== null}
        onOpenChange={(open) => !open && setCreatingPart(null)}
        initialName={creatingPart ?? undefined}
        onSaved={(id) => add(id)}
      />
      <ConfirmDialog
        open={confirm === "cancel"}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t("orders.cancelOrder")}
        description={t("orders.deleteBody")}
        confirmLabel={t("orders.cancelOrder")}
        destructive
        onConfirm={() =>
          cancel.mutate(
            { id: order.id },
            {
              onSuccess: () => {
                setConfirm(null);
                toast.success(t("orders.cancelled"));
              },
            },
          )
        }
      />
      <ConfirmDialog
        open={confirm === "delete"}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={t("common.deleteConfirmTitle", { name: order.name })}
        description={t("orders.deleteBody")}
        confirmLabel={t("common.delete")}
        destructive
        onConfirm={() =>
          remove.mutate(
            { id: order.id },
            {
              onSuccess: () => {
                toast.success(t("orders.deleted"));
                void navigate({ to: "/orders" });
              },
            },
          )
        }
      />
    </Page>
  );
}

function OrderLineRow({ line, order, editable }: { line: Line; order: Order; editable: boolean }) {
  const { t } = useTranslation();
  const format = useFormat();
  const trpc = useTRPC();
  const update = useMutation(trpc.orders.updateLine.mutationOptions());
  const remove = useMutation(trpc.orders.removeLine.mutationOptions());
  const undo = useMutation(trpc.orders.undoReceipt.mutationOptions());
  const [qty, setQty] = useState<number | null>(line.qtyOrdered);
  const [price, setPrice] = useState<number | null>(line.unitPriceMinor);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => setQty(line.qtyOrdered), [line.qtyOrdered]);
  useEffect(() => setPrice(line.unitPriceMinor), [line.unitPriceMinor]);

  const save = (next: { qty?: number | null; price?: number | null }) => {
    const q = next.qty === undefined ? qty : next.qty;
    const p = next.price === undefined ? price : next.price;
    if (timer.current) clearTimeout(timer.current);
    if (!q || q <= 0) return;
    timer.current = setTimeout(
      () =>
        update.mutate({
          id: line.id,
          data: {
            qtyOrdered: q,
            unitPriceMinor: p,
            workOrderId: line.workOrder?.id ?? null,
            note: line.note,
          },
        }),
      700,
    );
  };

  const complete = line.qtyReceived >= line.qtyOrdered;
  return (
    <li className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
      <Link
        to="/inventory/$partId"
        params={{ partId: line.partId }}
        className="group flex min-w-0 flex-1 items-center gap-3"
      >
        <FileImage fileId={line.coverFileId} alt="" icon={BoxesIcon} className="size-10" />
        <div className="min-w-0">
          <div className="truncate font-medium group-hover:underline">{line.partName}</div>
          <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
            {line.primaryNumber ? <span className="font-mono">{line.primaryNumber}</span> : null}
            {line.workOrder ? (
              <span>
                {t("orders.forWorkOrder")} {line.workOrder.title}
              </span>
            ) : null}
          </div>
        </div>
      </Link>
      <div className="flex flex-wrap items-center gap-2 pl-[52px] sm:flex-nowrap sm:pl-0">
        {order.status !== "draft" && order.status !== "cancelled" ? (
          <span
            className={cn(
              "rounded-md px-2 py-1 text-xs font-medium tabular",
              complete
                ? "bg-ok-bg text-ok-fg"
                : line.qtyReceived > 0
                  ? "bg-partial-bg text-partial-fg"
                  : "bg-muted text-muted-foreground",
            )}
          >
            {format.qtyNumber(line.qtyReceived, line.unit)}/{format.qty(line.qtyOrdered, line.unit)}
          </span>
        ) : null}
        {editable ? (
          <>
            <QuantityInput
              className="w-36"
              aria-label={t("orders.ordered")}
              unit={line.unit}
              value={qty}
              min={line.qtyReceived}
              onChange={(value) => {
                setQty(value);
                save({ qty: value });
              }}
            />
            <MoneyInput
              aria-label={t("orders.unitPrice")}
              className="w-28"
              currency={order.currency}
              showBasis={false}
              value={price}
              onChange={(value) => {
                setPrice(value);
                save({ price: value });
              }}
            />
          </>
        ) : (
          <span className="w-24 text-right text-sm text-muted-foreground tabular">
            {line.unitPriceMinor !== null ? format.money(line.unitPriceMinor, order.currency) : "–"}
          </span>
        )}
        <span className="w-24 text-right text-sm font-medium tabular">
          {line.lineTotalMinor !== null ? format.money(line.lineTotalMinor, order.currency) : "–"}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={t("common.more")}>
              <MoreHorizontalIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {line.sourceUrl ? (
              <DropdownMenuItem onSelect={() => openExternal(line.sourceUrl as string)}>
                <ExternalLinkIcon />
                {t("orders.openShop")}
              </DropdownMenuItem>
            ) : null}
            {line.receipts.length ? (
              <>
                <DropdownMenuLabel>{t("orders.receipts")}</DropdownMenuLabel>
                {line.receipts.map((receipt) => (
                  <DropdownMenuItem
                    key={receipt.movementId}
                    onSelect={() => undo.mutate({ movementId: receipt.movementId })}
                  >
                    <Undo2Icon />
                    {t("orders.undoReceipt")}: {format.qty(receipt.qty, line.unit)} ·{" "}
                    {format.shortDate(receipt.createdAt)}
                  </DropdownMenuItem>
                ))}
              </>
            ) : null}
            {editable && line.qtyReceived === 0 ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem destructive onSelect={() => remove.mutate({ id: line.id })}>
                  <Trash2Icon />
                  {t("common.remove")}
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}
